import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import { inputClass } from '../../pages/picture-ai/usePictureAi';

const MICROS_PER_UNIT = 1_000_000;
const TOKENS_PER_MTOK = 1_000_000;

const money = (micros, digits = 2) => micros === null || micros === undefined || Number.isNaN(micros)
  ? '—'
  : `${(Number(micros) / MICROS_PER_UNIT).toFixed(digits)} $`;

/**
 * What this configuration will cost to run, per month and per year.
 *
 * Token consumption is never shown or asked for: it comes from what the agent declares a picture
 * of its kind typically costs, and is replaced by the real average as soon as this configuration
 * has run anything. The only things worth deciding are the model, the monthly volume and the
 * transport, so those are the only inputs.
 *
 * Both transports are priced side by side rather than only the selected one: the batch discount is
 * the largest saving available here, and it is invisible to anyone who never thinks to try the
 * other setting. What it costs is a wait of up to 24 h, which is said next to the number.
 */
const CostEstimator = ({
  model, models, provider, onModelChange, onProviderChange,
  monthlyVolume, monthsObserved, transport, onTransportChange,
  usageEstimate, observedInputTokens, observedOutputTokens, pricedAnalyses,
  budgetCents, onBudgetChange, disabled,
}) => {
  const { t } = useTranslation();
  const [volume, setVolume] = useState(monthlyVolume ?? null);
  const [volumeTouched, setVolumeTouched] = useState(false);

  useEffect(() => { if (!volumeTouched) setVolume(monthlyVolume ?? null); }, [monthlyVolume, volumeTouched]);

  const simulated = provider === 'mock';
  const rate = (models || []).find(entry => entry.model === model) || null;
  const discount = transport === 'batch' ? (rate?.batch_discount ?? 1) : 1;

  const measured = pricedAnalyses > 0 && observedInputTokens > 0;
  const inputTokens = measured ? observedInputTokens : usageEstimate?.input_tokens;
  const outputTokens = measured ? observedOutputTokens : usageEstimate?.output_tokens;

  const unitAt = (multiplier) => (!simulated && rate && inputTokens > 0
    ? ((inputTokens / TOKENS_PER_MTOK) * rate.input_per_mtok
      + (outputTokens / TOKENS_PER_MTOK) * rate.output_per_mtok) * multiplier * MICROS_PER_UNIT
    : null);
  const monthlyAt = (multiplier) => {
    const unit = unitAt(multiplier);
    return unit !== null && Number.isFinite(volume) ? unit * volume : null;
  };

  const batchDiscount = rate?.batch_discount ?? 1;
  const rows = [
    { key: 'standard', label: t('Standard'), monthly: monthlyAt(1), note: t('Results within the minute.') },
    {
      key: 'batch',
      label: t('Batch'),
      monthly: monthlyAt(batchDiscount),
      note: t('Results within 24 h, at the provider’s pace.'),
      saving: batchDiscount < 1 ? Math.round((1 - batchDiscount) * 100) : null
    },
  ];

  const monthly = monthlyAt(discount);

  const budgetMicros = budgetCents === '' || budgetCents === null || budgetCents === undefined
    ? null
    : Number(budgetCents) * 10_000;
  const overBudget = monthly !== null && budgetMicros !== null && monthly > budgetMicros;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Model')}</span>
          <select className={inputClass} value={model} disabled={disabled} onChange={event => onModelChange(event.target.value)}>
            {(models || []).map(entry => <option key={entry.model} value={entry.model}>{entry.label}</option>)}
          </select>
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {rate && t('{{input}} $ / {{output}} $ per million tokens, input/output.', { input: rate.input_per_mtok, output: rate.output_per_mtok })}
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Pictures per month')}</span>
          <input
            type="number" min="0" className={inputClass} value={volume ?? ''} disabled={disabled}
            onChange={(event) => { setVolumeTouched(true); setVolume(event.target.value === '' ? null : Number(event.target.value)); }}
          />
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
            {volumeTouched && t('Manual value.')}
            {!volumeTouched && monthsObserved > 0 && t('Average over the last {{count}} complete months.', { count: monthsObserved })}
            {!volumeTouched && !monthsObserved && t('No history for this scope yet — enter the volume you expect.')}
          </span>
        </label>

        <div className="lg:col-span-2">
          <div className="mb-1 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-200">
            <span>{t('Transport')}</span>
            <span className="text-xs font-normal uppercase text-gray-500 dark:text-gray-400">
              {t('per month · per year')}
            </span>
          </div>
          <div role="radiogroup" aria-label={t('Transport')} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            {rows.map((row) => {
              const active = transport === row.key;
              return (
                <button
                  key={row.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled || !onTransportChange}
                  onClick={() => onTransportChange && onTransportChange(row.key)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition ${
                    active
                      ? 'bg-green-50 dark:bg-green-950/30'
                      : 'bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800'
                  } ${row.key === 'batch' ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}
                >
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition ${
                        active
                          ? 'border-green-600 bg-green-600 text-white'
                          : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                      }`}>
                        {active && (
                          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z" />
                          </svg>
                        )}
                      </span>
                      <span className={`text-sm ${active ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                        {row.label}
                      </span>
                      {row.saving && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/60 dark:text-green-200">
                          −{row.saving} %
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block pl-7 text-xs text-gray-500 dark:text-gray-400">{row.note}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-xl font-semibold text-gray-900 dark:text-white">{money(row.monthly)}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {row.monthly === null ? '—' : t('{{total}} per year', { total: money(row.monthly * 12) })}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {simulated && t('Simulated provider: nothing is sent and nothing is billed.')}
        {!simulated && !rate && t('This model has no rate configured, so no cost can be estimated.')}
        {!simulated && rate && (measured
          ? t('Based on what the last {{count}} analyses actually consumed.', { count: pricedAnalyses })
          : t('Based on the agent’s default consumption estimate — refined from the first analysis onwards.'))}
        {' · '}
        <button type="button" className="underline" disabled={disabled} onClick={() => onProviderChange(simulated ? 'openai' : 'mock')}>
          {simulated ? t('Switch back to the real provider') : t('Use the simulated provider')}
        </button>
      </p>

      <label className="block w-64">
        <span className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
          {t('Spending ceiling per cycle')}
          <InfoHint label={t('Spending ceiling per cycle')}>
            {t('Optional. Each run reserves its spending before analysing, so the worker stops enqueueing once the cycle reaches this ceiling instead of discovering it afterwards. It is an estimate based on the observed cost, not a hard cap.')}
          </InfoHint>
        </span>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" className={inputClass} value={budgetCents ?? ''} disabled={disabled}
            onChange={event => onBudgetChange(event.target.value)}
          />
          <span className="text-sm text-gray-500">{t('cents')}</span>
        </div>
      </label>

      {overBudget && (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {t('A month’s worth ({{total}}) exceeds the ceiling: it will be spread over several cycles.', { total: money(monthly) })}
        </p>
      )}
    </div>
  );
};

export default CostEstimator;
